import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _noop from "lodash/noop";
import React, { useMemo, useRef } from 'react';
import { useCombobox } from 'downshift';
import { ComboBoxContext } from "./ComboBoxContext";
import styled from 'styled-components';
import { autocompleteStrategy } from "./strategies";
/**
 * `ComboBox`
 *
 * A `ComboBox` composes an edit control and a keyboard navigable list of items
 * that are displayed relevant to the input or part of the input value.
 */

export var ComboBox = function ComboBox(_ref) {
  var children = _ref.children,
      defaultHighlightedIndex = _ref.defaultHighlightedIndex,
      _ref$initialInputValu = _ref.initialInputValue,
      initialInputValue = _ref$initialInputValu === void 0 ? '' : _ref$initialInputValu,
      initialIsOpen = _ref.initialIsOpen,
      isOpenControlProp = _ref.isOpen,
      _ref$items = _ref.items,
      items = _ref$items === void 0 ? [] : _ref$items,
      itemToString = _ref.itemToString,
      _ref$onInputValueChan = _ref.onInputValueChange,
      onInputValueChange = _ref$onInputValueChan === void 0 ? _noop : _ref$onInputValueChan,
      selectedItemProp = _ref.selectedItem,
      onStateChange = _ref.onStateChange,
      _ref$query = _ref.query,
      query = _ref$query === void 0 ? '' : _ref$query,
      onSelectedItemChange = _ref.onSelectedItemChange,
      _ref$stateReducer = _ref.stateReducer,
      stateReducer = _ref$stateReducer === void 0 ? autocompleteStrategy : _ref$stateReducer,
      other = _objectWithoutProperties(_ref, ["children", "defaultHighlightedIndex", "initialInputValue", "initialIsOpen", "isOpen", "items", "itemToString", "onInputValueChange", "selectedItem", "onStateChange", "query", "onSelectedItemChange", "stateReducer"]);

  var inputRef = useRef(null);
  var itemCount = items.length;

  var _useCombobox = useCombobox({
    defaultHighlightedIndex: defaultHighlightedIndex,
    initialInputValue: initialInputValue,
    initialIsOpen: initialIsOpen,
    isOpen: isOpenControlProp,
    items: items,
    itemToString: itemToString,
    onInputValueChange: onInputValueChange,
    onStateChange: onStateChange,
    stateReducer: stateReducer,
    onSelectedItemChange: onSelectedItemChange,
    selectedItem: selectedItemProp
  }),
      closeMenu = _useCombobox.closeMenu,
      getComboboxProps = _useCombobox.getComboboxProps,
      getInputProps = _useCombobox.getInputProps,
      getItemProps = _useCombobox.getItemProps,
      getLabelProps = _useCombobox.getLabelProps,
      getMenuProps = _useCombobox.getMenuProps,
      highlightedIndex = _useCombobox.highlightedIndex,
      inputValue = _useCombobox.inputValue,
      isOpen = _useCombobox.isOpen,
      openMenu = _useCombobox.openMenu,
      reset = _useCombobox.reset,
      selectedItem = _useCombobox.selectedItem,
      selectItem = _useCombobox.selectItem,
      setInputValue = _useCombobox.setInputValue;

  var comboBoxContext = useMemo(function () {
    return {
      closeMenu: closeMenu,
      getComboboxProps: getComboboxProps,
      getInputProps: getInputProps,
      getItemProps: getItemProps,
      getLabelProps: getLabelProps,
      getMenuProps: getMenuProps,
      highlightedIndex: highlightedIndex,
      inputValue: inputValue,
      isOpen: isOpen,
      query: query,
      openMenu: openMenu,
      reset: reset,
      selectedItem: selectedItem,
      selectItem: selectItem,
      setInputValue: setInputValue,
      inputRef: inputRef,
      itemCount: itemCount
    };
  }, [closeMenu, getComboboxProps, getInputProps, getItemProps, getLabelProps, getMenuProps, highlightedIndex, inputValue, isOpen, query, openMenu, reset, selectedItem, selectItem, setInputValue, inputRef, itemCount]);
  return /*#__PURE__*/React.createElement(ComboBoxContext.Provider, {
    value: comboBoxContext
  }, /*#__PURE__*/React.createElement(StyledComboBox, _extends({}, getComboboxProps({
    'aria-expanded': !!isOpen
  }), other), children));
};
var StyledComboBox = styled.div.withConfig({
  displayName: "ComboBox__StyledComboBox",
  componentId: "vapor__sc-1m88eau-0"
})(["position:relative;"]);